import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { withPageTracking } from "../../../services/postHog";
import { enqueueSnackbar } from "notistack";
import { Add, Upload, MoreVert, Description } from "@mui/icons-material";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import AssignmentSummaryCard from "../components/AssignmentSummaryCard";
import { AddClassesToGroupDialog } from "../components/AddClassesToGroupDialog";
import { AssignmentGroupUploadDialog } from "../components/AssignmentGroupUploadDialog";
import { ExportGroupResultsDialog } from "../components/ExportGroupResultsDialog";
import {
  useExportAssignmentGroupCoverSheets,
  useExportAssignmentGroupResults,
} from "../hooks/Exporters";
import { checkIfIsIndependentUser } from "../../auth/Utils";

const AssignmentGroupView = () => {
  const { assignmentGroupId } = useParams();
  const ApiClient = useApiClient();
  const { exportAssignmentGroupCoverSheets } =
    useExportAssignmentGroupCoverSheets();
  const { exportAssignmentGroupResults } = useExportAssignmentGroupResults();
  const [addClassesDialogOpen, setAddClassesDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [assignmentGroup, setAssignmentGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const isIndependentUser = checkIfIsIndependentUser();
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const fetchAssignmentGroup = async () => {
    try {
      setLoading(true);
      const response = await ApiClient.assignment.getAssignmentGroup({
        assignmentGroup: assignmentGroupId,
      });
      if (response) {
        setAssignmentGroup(response.assignmentGroup);
      }
    } catch (err) {
      console.error("Failed to fetch assignment group", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignmentGroup();
  }, [assignmentGroupId]);

  const handleAddClassesSuccess = (result) => {
    enqueueSnackbar(result.message, { variant: "success" });
    fetchAssignmentGroup();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadCoverSheets = async () => {
    await exportAssignmentGroupCoverSheets(assignmentGroup);
    handleMenuClose();
  };

  const handleExportResults = () => {
    setExportDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmExport = async (schools) => {
    await exportAssignmentGroupResults(assignmentGroup, schools);
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    handleMenuClose();
  };

  const handleUploadSuccess = (result) => {
    if (result.success) {
      enqueueSnackbar(
        "Upload successful. Processing booklets in background...",
        { variant: "success" },
      );
    }
    fetchAssignmentGroup();
  };

  if (loading) return <GeneralLoading />;
  if (error) return <Typography>Error loading assignment group</Typography>;

  if (!assignmentGroup) {
    return <Typography>Assignment group not found</Typography>;
  }

  const hasUnpublishedAssignments = assignmentGroup.assignments.some(
    (a) => !a.isPublished,
  );

  return (
    <FruitifyContainer>
      <AddClassesToGroupDialog
        open={addClassesDialogOpen}
        setOpen={setAddClassesDialogOpen}
        assignmentGroupId={assignmentGroupId}
        onSuccess={handleAddClassesSuccess}
        hasUnpublishedAssignments={hasUnpublishedAssignments}
      />
      <AssignmentGroupUploadDialog
        open={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        assignmentGroupId={assignmentGroupId}
        onSuccess={handleUploadSuccess}
      />
      <ExportGroupResultsDialog
        open={exportDialogOpen}
        setOpen={setExportDialogOpen}
        assignmentGroup={assignmentGroup}
        onConfirm={handleConfirmExport}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="h3" fontWeight="bold">
              {assignmentGroup.name}
            </Typography>
            <Chip
              label="Assignment Group"
              color="primary"
              size="small"
              variant="outlined"
            />
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {assignmentGroup.subject.name} -{" "}
            {assignmentGroup.educationLevel.level}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() =>
              navigate(
                `/assignments/submission-hub/group/${assignmentGroup.id}`,
              )
            }
          >
            Submission Hub
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClick}
            endIcon={<MoreVert />}
            disabled={assignmentGroup.assignments.length === 0}
          >
            Actions
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            <MenuItem onClick={handleDownloadCoverSheets}>
              <Description fontSize="small" sx={{ mr: 1 }} />
              Download All Cover Sheets
            </MenuItem>
            <MenuItem onClick={handleUploadClick}>
              <Upload fontSize="small" sx={{ mr: 1 }} />
              Upload Multi-Assignment Booklets
            </MenuItem>
            <Tooltip
              title={
                isIndependentUser && "Only available on school subscriptions"
              }
            >
              <span>
                <MenuItem
                  onClick={handleExportResults}
                  disabled={isIndependentUser}
                >
                  <FruitifyIcon
                    name={"CSVFile"}
                    fontSize="small"
                    sx={{ mr: 1 }}
                  />
                  Export Results
                </MenuItem>
              </span>
            </Tooltip>
          </Menu>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddClassesDialogOpen(true)}
            disabled={hasUnpublishedAssignments}
          >
            Add more classes
          </Button>
        </Stack>
      </Box>

      {assignmentGroup.assignments.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No assignments in this group yet. Click &quot;Add more
              classes&quot; to add assignments.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {assignmentGroup.assignments.map((assignment) => (
            <Grid key={assignment.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <AssignmentSummaryCard
                showSchoolName
                assignment={assignment}
                showAnswerCount
                displayMode="class"
              />
            </Grid>
          ))}
        </Grid>
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentGroupView, {
  feature: "assignment-group-view",
});
